import {
  SoftwareAuthorizationService,
  SoftwareCostService,
  SoftwareCostsOut,
  SoftwareMiniOut,
  SoftwareOut,
  SoftwareService,
  SoftwareUserCount,
} from '@/client'
import { useFetch } from '@/composables/useFetch'

export async function _getApplications(
  args: Parameters<
    typeof SoftwareService.getSoftwareListApiV1SoftwareSoftwareGet
  >[0] // Correctly type the argument
): Promise<{
  data: SoftwareOut[] | SoftwareMiniOut[]
  error: ReturnType<typeof useFetch>['error']['value']
  loading: boolean
}> {
  const { data, error, fetchData, loading } = useFetch(
    () => SoftwareService.getSoftwareListApiV1SoftwareSoftwareGet(args),
    null,
    { sendToast: false }
  )

  await fetchData()

  // Refine the return type based on `args.extended`
  if (args.extended === false) {
    return {
      data: data.value as SoftwareMiniOut[],
      error: error.value,
      loading: loading.value,
    }
  }

  return {
    data: data.value as SoftwareOut[],
    error: error.value,
    loading: loading.value,
  }
}

export async function _deleteApplication(
  args: Parameters<
    typeof SoftwareService.deleteSoftwareApiV1SoftwareSoftwareSoftwareIdDelete
  >[0] // Correctly type the argument
) {
  const { data, error, fetchData, loading } = useFetch(
    () =>
      SoftwareService.deleteSoftwareApiV1SoftwareSoftwareSoftwareIdDelete(args),
    null,
    { sendToast: false }
  )

  await fetchData()

  return { data: data.value, error: error.value, loading: loading.value }
}

export async function _putApplication(
  args: Parameters<
    typeof SoftwareService.putSoftwareApiV1SoftwareSoftwareSoftwareIdPut
  >[0] // Correctly type the argument
) {
  const { data, error, fetchData, loading } = useFetch(
    () => SoftwareService.putSoftwareApiV1SoftwareSoftwareSoftwareIdPut(args),
    null,
    { sendToast: false }
  )

  await fetchData()

  return { data: data.value, error: error.value, loading: loading.value }
}

export async function _getApplicationById(
  args: Parameters<
    typeof SoftwareService.getSoftwareApiV1SoftwareSoftwareSoftwareIdGet
  >[0] // Correctly type the argument
) {
  const { data, error, fetchData, loading } = useFetch(
    () => SoftwareService.getSoftwareApiV1SoftwareSoftwareSoftwareIdGet(args),
    null,
    { sendToast: false }
  )

  await fetchData()

  return { data: data.value, error: error.value, loading: loading.value }
}

export async function _addApplication(
  args: Parameters<
    typeof SoftwareAuthorizationService.postSoftwareApiV1SoftwareSoftwarePost
  >[0]
) {
  const { data, error, fetchData, loading } = useFetch(
    () =>
      SoftwareAuthorizationService.postSoftwareApiV1SoftwareSoftwarePost(args),
    null,
    { sendToast: false }
  )

  await fetchData()

  return { data: data.value, error: error.value, loading: loading.value }
}

// ----------------------------------------------------------------------
// Application User Count
// ----------------------------------------------------------------------

export async function _getApplicationUserCount(
  args: Parameters<
    typeof SoftwareService.getUserCountApiV1SoftwareUserCountGet
  >[0]
): Promise<{
  data: Record<string, SoftwareUserCount>
  error: ReturnType<typeof useFetch>['error']['value']
  loading: boolean
}> {
  const { data, error, fetchData, loading } = useFetch(
    () => SoftwareService.getUserCountApiV1SoftwareUserCountGet(args),
    null,
    { sendToast: false }
  )

  await fetchData()

  return {
    data: data.value as Record<string, SoftwareUserCount>,
    error: error.value,
    loading: loading.value,
  }
}

// ----------------------------------------------------------------------
// Application Costs
// ----------------------------------------------------------------------

export async function _getApplicationCosts(
  args: Parameters<typeof SoftwareCostService.getCostsApiV1SoftwareCostGet>[0]
): Promise<{
  data: SoftwareCostsOut
  error: ReturnType<typeof useFetch>['error']['value']
  loading: boolean
}> {
  const { data, error, fetchData, loading } = useFetch(
    () => SoftwareCostService.getCostsApiV1SoftwareCostGet(args),
    null,
    { sendToast: false }
  )

  await fetchData()

  return {
    data: data.value as SoftwareCostsOut,
    error: error.value,
    loading: loading.value,
  }
}
